import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const links = [
  { to: "/approach", label: "Approach", id: "nav-approach-link" },
  { to: "/services", label: "Services", id: "nav-services-link" },
  { to: "/work", label: "Work", id: "nav-work-link" },
  { to: "/contact", label: "Contact", id: "nav-contact-link" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.19, 1.0, 0.22, 1.0], delay: 0.1 }}
        className="fixed inset-x-0 top-0 z-50 border-b border-black/10 bg-background/60 backdrop-blur-xl"
        data-testid="main-navbar"
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 sm:px-12">
          <Link to="/" data-testid="nav-logo-link" className="group flex items-center">
            <img
              src="/cc-logo.png"
              alt="Creative Curve Communication"
              className="h-10 w-auto transition-opacity duration-300 group-hover:opacity-70"
              data-testid="nav-logo-image"
            />
          </Link>

          <nav className="hidden items-center gap-10 md:flex" data-testid="nav-links-desktop">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="group flex items-center gap-2 border border-foreground/30 px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:border-foreground hover:bg-foreground hover:text-background"
            >
              Begin a Conversation
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            className="text-foreground md:hidden"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-button"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-background/95 px-8 backdrop-blur-xl md:hidden"
            data-testid="nav-mobile-menu"
          >
            {[{ to: "/", label: "Home", id: "nav-mobile-home-link" }, ...links].map((l, i) => (
              <div key={l.to + l.label} className="overflow-hidden border-b border-black/10">
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.5, ease: [0.19, 1.0, 0.22, 1.0], delay: i * 0.07 }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={l.id}
                    className={`block py-5 font-syne text-4xl font-extrabold uppercase tracking-wide ${
                      location.pathname === l.to ? "text-primary" : "text-foreground"
                    }`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
